import React from "react";
import DoorDetailPage from "../../PreviewPdf/DoorDetailPage";
import DoubleDoorDetailPage from "../../PreviewPdf/DoubleDoorDetailPage";
import DoorPhotosPage from "../../PreviewPdf/DoorPhotosPage";
import DoorRemedialActionPage from "../../PreviewPdf/DoorRemedialActionPage";
import { resolvePageContext } from "../context/resolvePageContext";
import { PAGE_TYPES } from "../model/pageTypes";
import ActionPlanPage from "./pages/ActionPlanPage";
import CommunalDoorsPage from "./pages/CommunalDoorsPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import CoverPage from "./pages/CoverPage";
import IntroductionPage from "./pages/IntroductionPage";
import SummaryPage from "./pages/SummaryPage";

function ChunkLoadingPlaceholder() {
  return (
    <div className="page sheet report-preview__chunk-loading">
      <p>Loading section…</p>
    </div>
  );
}

function ActionPlanLoadingPage() {
  return (
    <div className="page sheet report-preview__chunk-loading">
      <p>Loading action plan…</p>
    </div>
  );
}

const PageRenderer = React.memo(function PageRenderer({
  page,
  chunkCache,
  routeParams,
}) {
  const ctx = resolvePageContext(page, chunkCache, routeParams);
  if (!ctx) return null;

  if (ctx.missingChunk) {
    return <ChunkLoadingPlaceholder />;
  }

  const { reportData, imageProxyBaseUrl, logodata, preReq, pageNum } = ctx;
  const propLogo = logodata.propShieldLogoBase64;

  switch (page.type) {
    case PAGE_TYPES.COVER:
      return (
        <CoverPage reportData={reportData} imageProxyBaseUrl={imageProxyBaseUrl} />
      );

    case PAGE_TYPES.INTRODUCTION:
      return <IntroductionPage pageNum={pageNum} propLogo={propLogo} />;

    case PAGE_TYPES.COMPANY_PROFILE:
    case PAGE_TYPES.COMPANY_PROFILE_INFO:
      return (
        <CompanyProfilePage
          reportData={reportData}
          imageProxyBaseUrl={imageProxyBaseUrl}
          pageNum={pageNum}
          propLogo={propLogo}
          pageType={page.type}
        />
      );

    case PAGE_TYPES.SUMMARY:
      return (
        <SummaryPage
          reportData={reportData}
          pageNum={pageNum}
          propLogo={propLogo}
        />
      );

    case PAGE_TYPES.COMMUNAL_DOORS:
      return (
        <CommunalDoorsPage
          reportData={reportData}
          imageProxyBaseUrl={imageProxyBaseUrl}
          pageNum={pageNum}
          propLogo={propLogo}
          communalPageIndex={page.meta?.communalPageIndex ?? 0}
        />
      );

    case PAGE_TYPES.DOOR_DETAIL:
      return (
        <DoorDetailPage
          key={page.doorId}
          doorData={ctx.door}
          complianceList={ctx.complianceList}
          imageProxyBaseUrl={imageProxyBaseUrl}
          preReq={preReq}
          pageNum={pageNum}
          logodata={logodata}
        />
      );

    case PAGE_TYPES.DOOR_DETAIL_DOUBLE: {
      const leaf = page.meta?.doubleLeafIndex ?? 0;
      const doublePageNums =
        leaf === 0 ? [pageNum, 0] : [0, pageNum];
      return (
        <DoubleDoorDetailPage
          key={`${page.doorId}-double-${leaf}`}
          doorData={ctx.door}
          complianceList={ctx.complianceList}
          imageProxyBaseUrl={imageProxyBaseUrl}
          preReq={preReq}
          pageNum={doublePageNums}
          logodata={logodata}
          singleLeafIndex={leaf}
        />
      );
    }

    case PAGE_TYPES.DOOR_PHOTO:
      return (
        <DoorPhotosPage
          key={`${page.doorId}-photo-${page.meta?.photoPageIndex}`}
          doorData={ctx.door}
          complianceList={ctx.complianceList}
          imageProxyBaseUrl={imageProxyBaseUrl}
          preReq={preReq}
          PhysicalList={ctx.physicalList}
          originalDoor={ctx.originalDoor}
          additionalPhotos={ctx.additionalPhotos}
          pageNum={[pageNum]}
          logodata={logodata}
          singlePageIndex={page.meta?.photoPageIndex ?? 0}
        />
      );

    case PAGE_TYPES.DOOR_REMEDIAL:
      return (
        <DoorRemedialActionPage
          key={`${page.doorId}-remedial-${page.meta?.remedialPageIndex}`}
          doorData={ctx.door}
          complianceList={ctx.complianceList}
          imageProxyBaseUrl={imageProxyBaseUrl}
          preReq={preReq}
          PhysicalList={ctx.physicalList}
          originalDoor={ctx.originalDoor}
          additionalPhotos={ctx.additionalPhotos}
          pageNum={[pageNum]}
          logodata={logodata}
          singlePageIndex={page.meta?.remedialPageIndex ?? 0}
        />
      );

    case PAGE_TYPES.ACTION_PLAN:
      return (
        <ActionPlanPage
          reportData={reportData}
          pageNum={pageNum}
          propLogo={propLogo}
          actionPlanPageIndex={page.meta?.actionPlanPageIndex ?? 0}
        />
      );

    case PAGE_TYPES.ACTION_PLAN_LOADING:
      return <ActionPlanLoadingPage />;

    default:
      return null;
  }
});

export default PageRenderer;
